import React, { FC, useEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { FallbackProps } from '@sparknz/journeys-common';
import { Box, Button, Icon, Link, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { trackErrorScreen } from 'journey/productDetails/utils';
import { MESSAGE_US } from 'shared/constants';

export const DEFAULT_ERROR_MESSAGE =
  'Please try again later or contact us for help.';

export const DEFAULT_ERROR_TITLE = `Sorry, we're unable to complete your request.`;

const FullHeightBox = styled(Box)`
  height: 100%;
`;

const ErrorMessageContainer = styled(Box)`
  height: 100%;
`;

interface ActionLayerErrorScreenProps extends FallbackProps {
  title?: string;
  message?: string;
  onDone: () => void;
}

const AlignedText = styled(Text)`
  text-align: center;
`;

const IconContainer = styled.div`
  ${({ theme: { palette, spacing } }) => css`
    padding: ${spacing.s3};
    border: 2px solid ${palette.baseColors.moeraki};
    border-radius: 50%;
    margin-bottom: ${spacing.s7};
  `}
`;

// This component could be reuse in future, if need to reuse, you have to make it dynamic.
// All things are already dynamic through props, just one thing is remain that analytics part.
const ActionLayerErrorScreen: FC<ActionLayerErrorScreenProps> = ({
  title = DEFAULT_ERROR_TITLE,
  message = DEFAULT_ERROR_MESSAGE,
  deslCode,
  deslMessage,
  onDone,
}) => {
  const {
    palette: {
      baseColors: { marangai },
    },
  } = useTheme();

  useEffect(() => {
    trackErrorScreen({
      errorMessage: `${title} ${message}`,
      internalErrorCode: deslCode,
      internalErrorMessage: deslMessage,
    });
  }, []);

  const handleDone = () => {
    trackCustomLink('Done');
    onDone();
  };

  return (
    <LinkRegion regionId="wearable.errorMessage">
      <FullHeightBox
        flexDirection="column"
        justifyContent="space-between"
        px={['s3', 's4', 's6']}
        pb="s6"
      >
        <ErrorMessageContainer
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <IconContainer>
            <Icon fill={marangai} name="warning_large" />
          </IconContainer>
          <AlignedText variant="subheadingL" mb="s4">
            {title}
          </AlignedText>
          <AlignedText variant="bodySMedium">{message}</AlignedText>
          <Link
            href={MESSAGE_US}
            linkVariant="standard"
            onClick={() => trackCustomLink('Message us')}
          >
            Message us
          </Link>
        </ErrorMessageContainer>
        <Button variant="default" onClick={handleDone}>
          Done
        </Button>
      </FullHeightBox>
    </LinkRegion>
  );
};

export default ActionLayerErrorScreen;
